import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { CampaignPulse } from '@/hooks/useMissionControl';

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

export interface CampaignLead {
  heyreach_lead_id: string;
  lead_name: string;
  lead_title: string | null;
  lead_company: string | null;
  lead_linkedin_url: string | null;
  exchange_count: number;
  latest_status: string;
  latest_incoming_at: string;
  latest_updated_at: string;
  latest_methodology: string;
  latest_confidence: number;
  has_pending_draft: boolean;
  decision_trace: Record<string, unknown> | null;
  analysis_report: Record<string, unknown> | null;
  strategy: Record<string, unknown> | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────

// os_campaign_stats is a security_invoker view over os_linkedin_drafts, whose
// RLS is USER-SEAT-scoped (migration 015: members see only their own
// drafts) — so detail/leads data is USER-dependent, not just
// organization-dependent. The authenticated user id must be part of the
// cache key or two members of the same organization can read/overwrite each
// other's cached rows (r3-F1 same-org cross-seat cache leak).
export const campaignDetailQueryKey = (
  userId: string | null,
  organizationId: string | null,
  campaignId: string | undefined,
): readonly ['campaign-detail', string | null, string | null, string | undefined] =>
  ['campaign-detail', userId, organizationId, campaignId];

export const campaignLeadsQueryKey = (
  userId: string | null,
  organizationId: string | null,
  campaignId: string | undefined,
): readonly ['campaign-leads', string | null, string | null, string | undefined] =>
  ['campaign-leads', userId, organizationId, campaignId];

export const useCampaignDetail = (campaignId: string | undefined) => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<CampaignPulse | null>({
    queryKey: campaignDetailQueryKey(userId, organizationId, campaignId),
    queryFn: async () => {
      if (!campaignId || !organizationId) return null;

      // See useCampaignPulse (useMissionControl.ts) for why this org filter
      // is required even though os_campaign_stats is security_invoker: RLS
      // visibility can briefly exceed the session's context org during a
      // membership transition, and this row's organization_id is what
      // downstream callers (e.g. the archive mutation's
      // campaignOrganizationId guard) trust as proof of ownership (F1).
      const { data, error } = await supabase
        .from('os_campaign_stats')
        .select('*')
        .eq('heyreach_campaign_id', campaignId)
        .eq('organization_id', organizationId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // not found
        throw error;
      }
      return data as CampaignPulse;
    },
    enabled: !!campaignId && userId !== null && organizationId !== null,
    staleTime: 30000,
  });
};

export const useCampaignLeads = (campaignId: string | undefined) => {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery<CampaignLead[]>({
    queryKey: campaignLeadsQueryKey(userId, organizationId, campaignId),
    queryFn: async () => {
      if (!campaignId || !organizationId) return [];

      // S9a r6-F1: heyreach_lead_id is a HeyReach-GLOBAL identifier, not a
      // tenant-scoped one — the same id can exist under two different
      // organizations, so no client-side post-filter on lead ids alone can
      // ever be a tenant-safe boundary (the previous ownership-lookup +
      // post-filter approach this replaced was exactly that: an
      // organization-scoped SET of lead ids used to filter an RPC that had
      // no organization predicate of its own). get_campaign_leads_v2 (see
      // supabase/migrations/20260806140000_campaign_archiving.sql) instead
      // binds p_organization_id directly inside BOTH of its CTEs — the
      // org-identified read IS the tenant boundary here, not a client-side
      // approximation of one. It is SECURITY INVOKER, so RLS still applies
      // on top of the explicit organization filter; this is additive
      // defense-in-depth, not a bypass. The legacy org-blind
      // get_campaign_leads(TEXT) was dropped in 20260808002000 after the
      // caller sweep found none left (S9b cleanup, completed).
      const { data, error } = await supabase.rpc('get_campaign_leads_v2', {
        p_organization_id: organizationId,
        p_campaign_id: campaignId,
      });

      if (error) throw error;
      return (data || []) as CampaignLead[];
    },
    enabled: !!campaignId && userId !== null && organizationId !== null,
    staleTime: 15000,
  });
};
